import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Typography,
} from "@mui/material";

interface FileDropZoneProps {
  value: File | null;
  accept?: string | null;
  disabled?: boolean;
  isPending?: boolean;
  label?: string;
  helperText?: string;
  error?: boolean;
  onChange: (file: File | null) => void;
  onRemove?: () => void;
}

const FileDropZone: React.FC<FileDropZoneProps> = ({
  value,
  accept,
  disabled = false,
  isPending = false,
  label = "Choose a file or drag it here",
  helperText,
  error = false,
  onChange,
  onRemove,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelected = (file: File | null) => {
    onChange(file);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (disabled) {
      return;
    }

    setIsDragging(false);

    const droppedFile = event.dataTransfer.files?.[0] ?? null;
    if (droppedFile) {
      handleFileSelected(droppedFile);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    handleFileSelected(selectedFile);
  };

  const handleClick = () => {
    if (disabled) {
      return;
    }
    inputRef.current?.click();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      inputRef.current?.click();
    }
  };

  return (
    <FormControl fullWidth error={error} disabled={disabled}>
      <Box
        sx={{
          border: 1,
          borderStyle: "dashed",
          borderColor: disabled ? "action.disabled" : isDragging ? "primary.main" : "divider",
          borderWidth: isDragging ? 2 : 1,
          borderRadius: 2,
          px: 2,
          py: 2,
          minHeight: 100,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          cursor: disabled ? "default" : "pointer",
          backgroundColor: disabled
            ? "action.disabledBackground"
            : isDragging
            ? "action.hover"
            : "transparent",
          transition: "border-color 0.2s ease, border-width 0.2s ease, background-color 0.2s ease",
          outline: "none",
          ":hover": disabled
            ? undefined
            : {
                borderColor: "primary.main",
                backgroundColor: "action.hover",
              },
        }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDragEnter={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!disabled) {
            setIsDragging(true);
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!disabled) {
            setIsDragging(true);
          }
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!disabled) {
            setIsDragging(false);
          }
        }}
        onDrop={handleDrop}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label={label}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
            <Typography variant="body2" color={value ? "text.primary" : "text.secondary"}>
              {value ? value.name : label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {value
                ? "Click to replace or drag a new file here."
                : "Drag and drop a file, or click to browse."}
            </Typography>
          </Box>
          {isPending && <CircularProgress size={20} />}
        </Box>

        {value && onRemove && (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              color="secondary"
              onClick={(event) => {
                event.stopPropagation();
                if (disabled) {
                  return;
                }
                onRemove();
              }}
            >
              Remove file
            </Button>
          </Box>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={accept ?? undefined}
          hidden
          disabled={disabled}
          onChange={handleFileInputChange}
        />
      </Box>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default FileDropZone;
